/**
 * On-demand PPTX export for Custom Dashboards (FB-003, #4414). Builds slides
 * from each widget's already-rendered snapshot (see WidgetExportSnapshot in
 * DashboardBuilder.tsx) — the same "export what's on screen, don't refetch"
 * rule the Unified Reporting CSV/Excel export already follows (reportExport.ts).
 *
 * Scheduled export and a handful of widget types (geo maps, image, divider)
 * are deliberately out of scope for this pass — see the PR description.
 * Pivot widgets DO get a slide: they export the same rows × columns crosstab
 * shown on screen (flank#4458 follow-up), built as a 'table' snapshot.
 * Those left out get NO slide at all (Earl, flank#4414: a slide whose
 * only content is "not included yet" isn't something to hand a client) — a
 * chart widget with no DATA is different and still gets its own slide (title
 * + "no data") so the deck's slide order matches the dashboard's for every
 * widget type this export DOES support.
 */
import PptxGenJS from 'pptxgenjs';

export interface PptxTableData {
  columns: string[];
  rows: Array<Record<string, unknown>>;
  /**
   * Raw numeric value per metric column (never the dimension column), keyed
   * the same as `rows` — `rows` holds formatMetricValue's display string
   * ("$1,234.56", "45.2%"), which CSV/Excel export can't SUM or sort as a
   * number. Optional and additive: PPTX rendering only ever reads `rows`, so
   * this doesn't touch it (flank#4553, CodeRabbit).
   */
  rawRows?: Array<Record<string, number | null>>;
}

export interface PptxMetricRow {
  label: string;
  value: string;
  /** Raw value behind `value` — same purpose as PptxTableData.rawRows. */
  raw?: number | null;
}

export interface PptxChartImage {
  data: string;
  /** The captured canvas's pixel size, so the slide can fit it without stretching. */
  width: number;
  height: number;
}

export type WidgetExportSnapshot =
  | { kind: 'text'; id: string; title: string; text: string }
  | { kind: 'metrics'; id: string; title: string; metrics: PptxMetricRow[] }
  | { kind: 'table'; id: string; title: string; table: PptxTableData }
  | {
      kind: 'chart';
      id: string;
      title: string;
      /** What's actually plotted — e.g. "Impressions by Channel", or "Impressions
       * — % of each day's peak" for a normalized time series. A chart's own
       * title is often just what the dashboard's author named the widget
       * ("Denver"), which says nothing about metric or dimension once it's a
       * static image on a slide with no tooltip to fall back on (Josh, live
       * testing #4414's own preview). */
      subtitle?: string;
      getImage: () => PptxChartImage | null;
    }
  | { kind: 'skip'; id: string; title: string; reason: string };

/** The box a chart image is placed in (inches) — same slot `addImage` used before. */
const CHART_BOX = { x: 0.6, y: 1.1, w: 8.8, h: 4 } as const;

/** Same box, shifted down and shortened to leave room for the subtitle line
 * addSubtitle draws right above it. */
const CHART_BOX_WITH_SUBTITLE = { x: 0.6, y: 1.45, w: 8.8, h: 3.65 } as const;

const SUBTITLE_OPTS = { x: 0.4, y: 0.85, w: 9.2, h: 0.35, fontSize: 12, italic: true, color: '666666' } as const;

const addSubtitle = (slide: PptxGenJS.Slide, subtitle: string) => {
  slide.addText(subtitle, SUBTITLE_OPTS);
};

/**
 * Fit an image's pixel aspect ratio inside CHART_BOX without stretching it,
 * centered on whichever axis has slack. `addImage`'s own `sizing: 'contain'`
 * needs a matching `w`/`h` box anyway, so this computes that box directly
 * instead (CodeRabbit review, PptxGenJS forces the aspect ratio the `w`/`h`
 * it's given implies — the captured chart was 477×160/477×140px, ~3:1, forced
 * into an 8.8"×4" ~2.2:1 box, which is what stretched it).
 */
export function fitWithinBox(
  imageWidth: number,
  imageHeight: number,
  box: { x: number; y: number; w: number; h: number } = CHART_BOX,
): { x: number; y: number; w: number; h: number } {
  const boxRatio = box.w / box.h;
  const imageRatio = imageWidth / imageHeight;
  const { w, h } = imageRatio > boxRatio ? { w: box.w, h: box.w / imageRatio } : { w: box.h * imageRatio, h: box.h };
  return { x: box.x + (box.w - w) / 2, y: box.y + (box.h - h) / 2, w, h };
}

const TITLE_OPTS = { x: 0.4, y: 0.3, w: 9.2, h: 0.6, fontSize: 20, bold: true, color: '111111' } as const;

const addTitle = (slide: PptxGenJS.Slide, title: string) => {
  slide.addText(title, TITLE_OPTS);
};

const addTable = (slide: PptxGenJS.Slide, table: PptxTableData) => {
  if (table.rows.length === 0) {
    slide.addText('No data for the current filters.', {
      x: 0.4,
      y: 1.1,
      w: 9.2,
      h: 0.5,
      fontSize: 12,
      color: '666666',
    });
    return;
  }
  const header = table.columns.map(c => ({
    text: c,
    options: { bold: true, fill: { color: 'F3F4F6' }, color: '111111', fontSize: 10 },
  }));
  const body = table.rows.map(row =>
    table.columns.map(c => ({ text: String(row[c] ?? ''), options: { fontSize: 10 } })),
  );
  slide.addTable([header, ...body], { x: 0.4, y: 1.1, w: 9.2, autoPage: true, autoPageRepeatHeader: true });
};

/** One dashboard export, in the widget order shown on screen. */
export function buildDashboardPptx(dashboardTitle: string, sections: WidgetExportSnapshot[]): PptxGenJS {
  const pptx = new PptxGenJS();
  pptx.defineLayout({ name: 'RIBEYE_16x9', width: 10, height: 5.63 });
  pptx.layout = 'RIBEYE_16x9';

  const cover = pptx.addSlide();
  cover.addText(dashboardTitle, { x: 0.6, y: 2.2, w: 8.8, h: 1, fontSize: 32, bold: true, color: '111111' });
  cover.addText(new Date().toLocaleDateString(), { x: 0.6, y: 3.1, w: 8.8, h: 0.5, fontSize: 14, color: '666666' });

  for (const section of sections) {
    // No slide at all for a widget type this export doesn't support — a slide
    // whose only content is "not included yet" isn't client-ready (Earl,
    // flank#4414). A supported type with no current data is different and
    // still gets its slide, via the 'metrics'/'table' empty-state below.
    if (section.kind === 'skip') continue;

    const slide = pptx.addSlide();
    addTitle(slide, section.title);

    switch (section.kind) {
      case 'text':
        slide.addText(section.text || '(empty)', { x: 0.4, y: 1.1, w: 9.2, h: 3.8, fontSize: 14, color: '333333' });
        break;
      case 'metrics': {
        if (section.metrics.length === 0) {
          slide.addText('No data for the current filters.', {
            x: 0.4,
            y: 1.1,
            w: 9.2,
            h: 0.5,
            fontSize: 12,
            color: '666666',
          });
          break;
        }
        const rows = section.metrics.map(m => [
          { text: m.label, options: { bold: true, fontSize: 12 } },
          { text: m.value, options: { fontSize: 12 } },
        ]);
        slide.addTable(rows, { x: 0.4, y: 1.1, w: 9.2, colW: [5, 4.2] });
        break;
      }
      case 'table':
        addTable(slide, section.table);
        break;
      case 'chart': {
        if (section.subtitle) addSubtitle(slide, section.subtitle);
        const image = section.getImage();
        if (image) {
          const box = section.subtitle ? CHART_BOX_WITH_SUBTITLE : CHART_BOX;
          slide.addImage({ data: image.data, ...fitWithinBox(image.width, image.height, box) });
        } else {
          slide.addText('Chart image was not available at export time.', {
            x: 0.4,
            y: 1.1,
            w: 9.2,
            h: 0.5,
            fontSize: 12,
            color: '666666',
          });
        }
        break;
      }
    }
  }

  return pptx;
}

/** Filesystem-safe file name for the exported deck. */
export const pptxFileName = (dashboardTitle: string): string =>
  `${dashboardTitle.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '') || 'dashboard'}.pptx`;
